import React, { useState } from 'react';
import ReportcenterFilter from 'App/pages/Reportcenter/ReportcenterContent/ReportcenterFilter/ReportcenterFilter';
import { Checkbox, DateInput, Flex, Input, Text } from '@upsales/components';
import { useTranslation } from 'Components/Helpers/translate';
import BemClass from '@upsales/components/Utils/bemClass';
import { ChangeType } from 'App/babel/resources/ClientChangeLog';
import { getChangeString } from './ClientChangeLog';
import moment from 'moment';

declare const Tools: any;

export const UserFilter = ({ filter, onChange }: { filter: number[]; onChange: (filter: any) => void }) => {
	const classes = new BemClass('ClientChangeLog');
	const { t } = useTranslation();

	const users = Tools.AppService.getUsers(undefined, true);

	const [search, setSearch] = useState('');
	return (
		<ReportcenterFilter
			icon="user"
			resetFilter={() => {
				onChange([]);
			}}
			className={classes.elem('userFilter').b()}
			renderSelected={() =>
				filter.length > 0
					? `${t(filter.length === 1 ? 'default.user' : 'default.users')}: ${filter.length} ${t(
							'default.selected'
					  ).toLowerCase()}`
					: `${t('default.user')}: ${t('reportcenter.filter.notActive')}`
			}
			placeholder={`${t('default.user')}: ${t('reportcenter.filter.notActive')}`}
			value={filter}
			updateFilterHeight={() => setSearch('')}
			values={{ User: { value: filter, comparison: 'eq' } }}
			renderCustomDropdown={() => (
				<Flex direction="column">
					<Flex space="plm prm mtm mbm">
						<Input
							placeholder={t('default.search')}
							onChange={e => {
								setSearch(e.target.value);
							}}
							onClear={() => setSearch('')}
						/>
					</Flex>
					{users.map((user: any) => {
						if (!user.name) {
							return null;
						}

						if (search && !user.name.toLowerCase().includes(search.toLowerCase())) {
							return null;
						}

						const checked = filter.includes(user.id);

						return (
							<Flex
								className={classes.elem('userFilterItem').b()}
								key={user.id}
								alignItems="center"
								gap="u1"
								space="plm prm ptm pbm"
								onClick={() => {
									if (checked) {
										onChange(filter.filter(id => id !== user.id));
									} else {
										onChange([...filter, user.id]);
									}
								}}
							>
								<Checkbox checked={checked} />
								<Text>{user.name}</Text>
							</Flex>
						);
					})}
				</Flex>
			)}
		/>
	);
};

export const DateFilter = ({
	filter,
	onChange
}: {
	filter: { start?: any; end?: any };
	onChange: (filter: any) => void;
}) => {
	const classes = new BemClass('ClientChangeLog');
	const { t } = useTranslation();

	return (
		<>
			<DateInput
				closeOnSelect
				className={classes.elem('dateInput').b()}
				placeholder={t('default.from')}
				max={filter.end ?? new Date()}
				value={filter.start}
				onChange={e => {
					onChange({ ...filter, start: e.target.value });
				}}
				onClear={() => {
					onChange({ ...filter, start: undefined });
				}}
			/>
			<DateInput
				closeOnSelect
				className={classes.elem('dateInput').b()}
				placeholder={t('default.to')}
				min={filter.start}
				max={new Date()}
				value={filter.end}
				onChange={e => {
					onChange({ ...filter, end: moment(e.target.value).endOf('day').toDate() });
				}}
				onClear={() => {
					onChange({ ...filter, end: undefined });
				}}
			/>
		</>
	);
};

// Map ChangeType to standard field names in metadata
const changeTypeToFieldName: Partial<Record<ChangeType, string>> = {
	[ChangeType.NAME_CHANGED]: 'Name',
	[ChangeType.NOTES_CHANGED]: 'Notes',
	[ChangeType.PHONE_CHANGED]: 'Phone',
	[ChangeType.FAX_CHANGED]: 'Fax',
	[ChangeType.WEBPAGE_CHANGED]: 'Www',
	[ChangeType.ORG_NO_CHANGED]: 'OrgNo',
	[ChangeType.COMPANY_FORM_CHANGED]: 'CompanyForm',
	[ChangeType.SNI_CODE_CHANGED]: 'SniCode',
	[ChangeType.DUNS_NO_CHANGED]: 'DunsNo',
	[ChangeType.CFAR_CHANGED]: 'Cfar',
	[ChangeType.CREDIT_RATING_CHANGED]: 'CreditRating',
	[ChangeType.PROSPECTING_CREDIT_RATING_CHANGED]: 'ProspectingCreditRating',
	[ChangeType.TURNOVER_CHANGED]: 'Turnover',
	[ChangeType.PROFIT_CHANGED]: 'Profit',
	[ChangeType.NO_EMPLOYEES_CHANGED]: 'NoEmployees',
	[ChangeType.FACEBOOK_CHANGED]: 'Facebook',
	[ChangeType.TWITTER_CHANGED]: 'Twitter',
	[ChangeType.LINKEDIN_CHANGED]: 'LinkedIn',
	[ChangeType.SIC_CODE_CHANGED]: 'SicCode',
	[ChangeType.NAICS_CODE_CHANGED]: 'NaicsCode',
	[ChangeType.UK_SIC_CODE_CHANGED]: 'UKSicCode',
	[ChangeType.NACE_CODE_CHANGED]: 'NaceCode',
	[ChangeType.ABOUT_CHANGED]: 'About',
	[ChangeType.ACTIVE_CHANGED]: 'Status',
	[ChangeType.IS_MONITORED_CHANGED]: 'IsMonitored'
};

const isChangeTypeActive = (type: ChangeType): boolean => {
	// Always show these change types (they don't have corresponding standard fields)
	const alwaysShowTypes = [
		ChangeType.ADDRESS_CHANGED,
		ChangeType.PARENT_CHANGED,
		ChangeType.OPERATIONAL_ACCOUNT_CHANGED,
		ChangeType.JOURNEY_STEP_CHANGED,
		ChangeType.PRICE_LIST_CHANGED,
		ChangeType.ACCOUNT_MANAGER_CHANGED,
		ChangeType.CATEGORIES_CHANGED,
		ChangeType.CAMPAIGNS_CHANGED,
		ChangeType.CUSTOM_FIELD_CHANGED
	];

	if (alwaysShowTypes.includes(type)) {
		return true;
	}

	// Check if the corresponding standard field is active
	const fieldName = changeTypeToFieldName[type];
	if (!fieldName) {
		// If no mapping exists, show it by default
		return true;
	}

	const standardFields = Tools.AppService.getMetadata()?.standardFields?.Client;
	if (!standardFields) {
		// If metadata not available, show it by default
		return true;
	}

	const field = standardFields[fieldName];
	if (!field) {
		// If field doesn't exist in metadata, show it by default
		return true;
	}

	// Show if field is active
	return !!field.active;
};

export const ChangeTypeFilter = ({ filter, onChange }: { filter: string[]; onChange: (filter: any) => void }) => {
	const classes = new BemClass('ClientChangeLog');
	const { t } = useTranslation();

	const activeChangeTypes = Object.values(ChangeType).filter(type => isChangeTypeActive(type as ChangeType));

	return (
		<ReportcenterFilter
			icon="th-list"
			resetFilter={() => {
				onChange([]);
			}}
			className={classes.elem('changeTypeFilter').b()}
			renderSelected={() =>
				filter.length > 0
					? `${t(filter.length === 1 ? 'auditLog.changeType' : 'auditLog.changeTypes')}: ${filter.length} ${t(
							'default.selected'
					  ).toLowerCase()}`
					: `${t('auditLog.changeType')}: ${t('reportcenter.filter.notActive')}`
			}
			placeholder={`${t('auditLog.changeType')}: ${t('reportcenter.filter.notActive')}`}
			value={filter}
			values={{}}
			renderCustomDropdown={() => (
				<Flex direction="column">
					{activeChangeTypes.map(type => {
						const checked = filter.includes(type);

						return (
							<Flex
								className={classes.elem('changeTypeFilterItem').b()}
								key={'filter' + type}
								space="plm prm ptm pbm"
								alignItems="center"
								gap="u1"
								onClick={() => {
									if (checked) {
										onChange(filter.filter(id => id !== type));
									} else {
										onChange([...filter, type]);
									}
								}}
							>
								<Checkbox checked={checked} />
								<Text>{t(getChangeString(type as ChangeType))}</Text>
							</Flex>
						);
					})}
				</Flex>
			)}
		/>
	);
};
